<script setup lang="ts">
type CuisineTone = "amber" | "sky" | "teal" | "rose";

interface Restaurant {
  name: string;
  cuisine: string;
  cuisine_tone?: CuisineTone;
  description: string;
  location?: string;
  hours?: string;
  hours_seasonal?: boolean;
  menu_url: string;
  image: string;
  image_alt?: string;
}

interface FootnoteLink {
  label: string;
  url: string;
}

interface RestaurantsBlock {
  header: string;
  intro?: string;
  footnote?: string;
  footnote_links?: FootnoteLink[];
  items: Restaurant[];
}

defineProps<{
  block: RestaurantsBlock;
}>();

const { t } = useI18n();

const toneClass: Record<CuisineTone, string> = {
  amber:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  sky: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
  teal: "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200",
  rose: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
};

function pillClass(tone?: CuisineTone) {
  return toneClass[tone ?? "amber"];
}
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-rose-50/30 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-rose-200/25 blur-3xl dark:bg-rose-500/10"
    />

    <div data-reveal class="relative mx-auto w-full max-w-7xl px-4 py-8 md:py-12">
      <header class="mb-6 md:mb-8">
        <span
          class="font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          {{ t("amenities.restaurants.eyebrow") }}
        </span>
        <h2
          class="mt-3 max-w-5xl font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="mt-3 max-w-3xl font-body text-base leading-relaxed text-slate-600 md:mt-4 md:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-5">
        <article
          v-for="venue in block.items"
          :key="venue.name"
          class="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-slate-700 dark:bg-slate-800/60"
        >
          <div class="relative aspect-[4/3] overflow-hidden">
            <NuxtImg
              :src="venue.image"
              :alt="venue.image_alt ?? venue.name"
              loading="lazy"
              width="600"
              height="450"
              sizes="sm:50vw md:33vw"
              class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <span
              :class="[
                'absolute left-3 top-3 inline-flex items-center rounded-full px-2.5 py-1 font-body text-[10px] font-semibold uppercase tracking-[0.18em] backdrop-blur-sm',
                pillClass(venue.cuisine_tone),
              ]"
            >
              {{ venue.cuisine }}
            </span>
          </div>

          <div class="flex flex-1 flex-col p-5 md:p-6">
            <h3
              class="font-display text-xl italic text-slate-900 md:text-2xl dark:text-white"
            >
              {{ venue.name }}
            </h3>
            <p
              class="mt-2 font-body text-sm leading-relaxed text-slate-600 dark:text-slate-300"
            >
              {{ venue.description }}
            </p>

            <div class="mt-auto pt-4">
              <dl
                v-if="venue.location || venue.hours || venue.hours_seasonal"
                class="flex flex-wrap gap-x-4 gap-y-2 font-body text-xs text-slate-500 dark:text-slate-400"
              >
                <div
                  v-if="venue.location"
                  class="inline-flex items-center gap-1.5"
                >
                  <Icon
                    name="lucide:map-pin"
                    class="size-3.5 shrink-0 text-amber-500 dark:text-amber-400"
                    aria-hidden="true"
                  />
                  <dt class="sr-only">
                    {{ t("amenities.restaurants.meta.location") }}
                  </dt>
                  <dd>{{ venue.location }}</dd>
                </div>
                <div
                  v-if="venue.hours || venue.hours_seasonal"
                  class="inline-flex items-center gap-1.5"
                >
                  <Icon
                    name="lucide:clock"
                    class="size-3.5 shrink-0 text-amber-500 dark:text-amber-400"
                    aria-hidden="true"
                  />
                  <dt class="sr-only">
                    {{ t("amenities.restaurants.meta.hours") }}
                  </dt>
                  <dd>
                    {{
                      venue.hours ?? t("amenities.restaurants.hours_seasonal")
                    }}
                  </dd>
                </div>
              </dl>

              <a
                :href="venue.menu_url"
                target="_blank"
                rel="noopener noreferrer"
                class="mt-4 inline-flex items-center justify-center gap-2 self-stretch rounded-xl border border-amber-300 bg-amber-50/50 px-4 py-2.5 font-body text-sm font-semibold text-amber-700 transition-colors hover:border-amber-400 hover:bg-amber-100 hover:text-amber-900 dark:border-amber-900/60 dark:bg-amber-900/10 dark:text-amber-300 dark:hover:bg-amber-900/30 dark:hover:text-amber-200"
                :aria-label="`${t('amenities.restaurants.view_menu')} — ${venue.name}`"
              >
                {{ t("amenities.restaurants.view_menu") }}
                <Icon
                  name="lucide:external-link"
                  class="size-4 shrink-0"
                  aria-hidden="true"
                />
              </a>
            </div>
          </div>
        </article>
      </div>

      <p
        v-if="block.footnote"
        class="mt-5 inline-flex items-start gap-2 font-body text-sm text-slate-600 md:mt-6 dark:text-slate-300"
      >
        <Icon
          name="lucide:info"
          class="mt-0.5 size-4 shrink-0 text-amber-500"
          aria-hidden="true"
        />
        <span>
          {{ block.footnote }}
          <template v-if="block.footnote_links?.length">
            <a
              v-for="link in block.footnote_links"
              :key="link.url"
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="ml-1 inline-flex items-center gap-1 font-semibold text-amber-700 transition-colors hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200"
            >
              {{ link.label }}
              <Icon
                name="lucide:external-link"
                class="size-3.5 shrink-0"
                aria-hidden="true"
              />
            </a>
          </template>
        </span>
      </p>
    </div>
  </section>
</template>
